/**
 * Batch 18-D2 — Training Recommendation Caps (Guardrails).
 *
 * This module owns the hard-rule guardrails that bound any
 * recommendation set returned by the training recommendation engine:
 *
 *   - Max 3 total recommendations
 *   - Max 1 per topic cluster
 *   - Max 1 per provider (unless manager override)
 *   - Max 1 paid (unless manager override)
 *   - Diversity bias (15% penalty for repeat provider when scores are
 *     within 15% of each other)
 *
 * Behavior is preserved BIT-FOR-BIT from
 * `src/utils/trainingRecommendationEngine.ts` prior to Batch 18-D2.
 * Constants, control flow, and thresholds are unchanged — only the
 * file location changed. `trainingRecommendationEngine.ts` re-exports
 * `applyRecommendationCaps` from this module to preserve its public
 * API; existing consumers (`useSmartRecommendations.ts` and
 * `tests/unit/trainingRecommendationEngine.test.ts`) continue to
 * import from `@/utils/trainingRecommendationEngine` unchanged.
 *
 * ScoredArtifact is imported as a TYPE ONLY from the engine. TypeScript
 * elides type-only imports at compile time, so there is no runtime
 * module cycle: at runtime, only the engine imports from this file.
 */

import type { ScoredArtifact } from '@/utils/trainingRecommendationEngine';

// ─────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────

export interface RecommendationCaps {
  maxTotal: number;
  maxPerCluster: number;
  maxPerProvider: number;
  maxPaid: number;
}

// ─────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────

export const DEFAULT_CAPS: RecommendationCaps = {
  maxTotal: 3,
  maxPerCluster: 1,
  maxPerProvider: 1,
  maxPaid: 1,
};

// ─────────────────────────────────────────────────────────────────────
// Diversity bias (Phase 4)
//
// When two scored artifacts have similar scores AND share a provider,
// the second one takes a 15% penalty so a single provider cannot
// dominate the top of the list. Re-sorts after the penalty is applied
// so the final order reflects the adjusted scores.
//
// Kept private to this module — it is only ever called by
// `applyRecommendationCaps` below.
// ─────────────────────────────────────────────────────────────────────

function applyDiversityBias(scored: ScoredArtifact[], threshold = 0.15): ScoredArtifact[] {
  if (scored.length <= 1) return scored;

  const result = [...scored];
  const seenProviders = new Set<string>();

  for (let i = 0; i < result.length; i++) {
    const current = result[i];
    const providerId = current.artifact.provider?.id || current.artifact.provider_name;

    if (seenProviders.has(providerId)) {
      // Apply small penalty to repeated providers when scores are close
      const prevScore = i > 0 ? result[i - 1].score : current.score;
      const scoreDiff = Math.abs(prevScore - current.score) / Math.max(prevScore, 0.01);

      if (scoreDiff < threshold) {
        // Scores are close - apply diversity penalty
        result[i] = {
          ...current,
          score: current.score * 0.85, // 15% penalty for repeated provider
        };
      }
    }

    seenProviders.add(providerId);
  }

  // Re-sort after applying diversity bias
  return result.sort((a, b) => b.score - a.score);
}

// ─────────────────────────────────────────────────────────────────────
// Cap enforcement
//
// HARD RULES — see header. Manager override only relaxes the provider
// and paid caps; `maxTotal` and `maxPerCluster` are absolute.
// ─────────────────────────────────────────────────────────────────────

/**
 * Apply all recommendation caps (HARD RULES)
 * - Max 3 total
 * - Max 1 per topic cluster
 * - Max 1 per provider (unless manager override)
 * - Max 1 paid (unless manager override)
 * Phase 4: Diversity bias applied before caps
 */
export function applyRecommendationCaps(
  scored: ScoredArtifact[],
  options?: {
    managerOverride?: boolean;
    caps?: Partial<RecommendationCaps>;
    applyDiversity?: boolean; // Phase 4: Enable diversity bias
  }
): ScoredArtifact[] {
  const { managerOverride = false, caps: customCaps, applyDiversity = true } = options || {};

  // Phase 4: Apply diversity bias before cap enforcement
  const diversified = applyDiversity ? applyDiversityBias(scored) : scored;

  const caps: RecommendationCaps = {
    ...DEFAULT_CAPS,
    ...customCaps,
    // Manager override relaxes provider and paid caps
    maxPerProvider: managerOverride ? 999 : (customCaps?.maxPerProvider ?? DEFAULT_CAPS.maxPerProvider),
    maxPaid: managerOverride ? 999 : (customCaps?.maxPaid ?? DEFAULT_CAPS.maxPaid),
  };

  const result: ScoredArtifact[] = [];
  const clusterCounts = new Map<string, number>();
  const providerCounts = new Map<string, number>();
  let paidCount = 0;

  for (const item of diversified) {
    // Check total cap
    if (result.length >= caps.maxTotal) {
      break;
    }

    const cluster = item.topicCluster || 'general';
    const providerId = item.artifact.provider?.id || item.artifact.provider_name;
    const isPaid = item.artifact.access_type !== 'free';

    // Check cluster cap
    if ((clusterCounts.get(cluster) || 0) >= caps.maxPerCluster) {
      continue;
    }

    // Check provider cap (GUARDRAIL: max 1 per provider unless manager override)
    if ((providerCounts.get(providerId) || 0) >= caps.maxPerProvider) {
      continue;
    }

    // Check paid cap (GUARDRAIL: max 1 paid unless manager override)
    if (isPaid && paidCount >= caps.maxPaid) {
      continue;
    }

    // Passed all caps - include this recommendation
    result.push(item);
    clusterCounts.set(cluster, (clusterCounts.get(cluster) || 0) + 1);
    providerCounts.set(providerId, (providerCounts.get(providerId) || 0) + 1);
    if (isPaid) {
      paidCount++;
    }
  }

  return result;
}
